import type { MessageDescriptor } from "react-intl";

export type IntegrationChannelStatus = "available" | "coming-soon";

export type IntegrationChannel = {
  id: string;
  name: MessageDescriptor;
  iconLabel: string;
  status: IntegrationChannelStatus;
  description: MessageDescriptor;
  note: MessageDescriptor;
  accentClass: string;
};

export const INTEGRATION_CHANNELS = [
  {
    id: "instagram",
    name: {
      id: "integrations.catalog.instagram.name",
      defaultMessage: "Instagram",
    },
    iconLabel: "IG",
    status: "available",
    description: {
      id: "integrations.catalog.instagram.description",
      defaultMessage:
        "Auto-publish approved posts, carousels, reels, and stories to your IG business account.",
    },
    note: {
      id: "integrations.catalog.instagram.note",
      defaultMessage:
        "Choose the connection path that matches how this Instagram account is managed.",
    },
    accentClass:
      "border-pink-500/30 bg-gradient-to-br from-pink-500/25 via-rose-500/15 to-transparent text-pink-700 dark:text-pink-300",
  },
  {
    id: "linkedin",
    name: {
      id: "integrations.catalog.linkedin.name",
      defaultMessage: "LinkedIn",
    },
    iconLabel: "in",
    status: "coming-soon",
    description: {
      id: "integrations.catalog.linkedin.description",
      defaultMessage:
        "Schedule and publish founder-led posts to your LinkedIn page or personal profile.",
    },
    note: {
      id: "integrations.catalog.linkedin.note",
      defaultMessage: "Company pages and founder-led distribution are on the roadmap.",
    },
    accentClass:
      "border-sky-500/30 bg-gradient-to-br from-sky-500/25 via-sky-500/15 to-transparent text-sky-700 dark:text-sky-300",
  },
  {
    id: "x",
    name: {
      id: "integrations.catalog.x.name",
      defaultMessage: "X (Twitter)",
    },
    iconLabel: "X",
    status: "coming-soon",
    description: {
      id: "integrations.catalog.x.description",
      defaultMessage: "Post threads and single tweets straight from your approval queue.",
    },
    note: {
      id: "integrations.catalog.x.note",
      defaultMessage: "Text-first distribution is planned, including scheduling and approvals.",
    },
    accentClass:
      "border-slate-900/20 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white dark:border-white/20",
  },
  {
    id: "tiktok",
    name: {
      id: "integrations.catalog.tiktok.name",
      defaultMessage: "TikTok",
    },
    iconLabel: "TT",
    status: "coming-soon",
    description: {
      id: "integrations.catalog.tiktok.description",
      defaultMessage: "Auto-publish short-form video content as soon as it's approved.",
    },
    note: {
      id: "integrations.catalog.tiktok.note",
      defaultMessage: "Short-form video publishing and asset mapping are coming soon.",
    },
    accentClass:
      "border-zinc-950/20 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-800 text-white dark:border-white/20",
  },
  {
    id: "blog",
    name: {
      id: "integrations.catalog.blog.name",
      defaultMessage: "Blog (RSS / Webhook)",
    },
    iconLabel: "RSS",
    status: "coming-soon",
    description: {
      id: "integrations.catalog.blog.description",
      defaultMessage:
        "Push approved long-form posts to WordPress, Webflow, or any custom blog endpoint.",
    },
    note: {
      id: "integrations.catalog.blog.note",
      defaultMessage: "Blog publishing will launch once webhook destinations are ready.",
    },
    accentClass:
      "border-slate-700/20 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-600 text-white dark:border-white/20",
  },
  {
    id: "email",
    name: {
      id: "integrations.catalog.email.name",
      defaultMessage: "Email",
    },
    iconLabel: "mail",
    status: "coming-soon",
    description: {
      id: "integrations.catalog.email.description",
      defaultMessage: "Sync newsletters and broadcasts with Mailchimp, Resend, or Customer.io.",
    },
    note: {
      id: "integrations.catalog.email.note",
      defaultMessage: "Email handoff is planned after publishing approvals stabilize.",
    },
    accentClass:
      "border-violet-500/30 bg-gradient-to-br from-violet-600 via-violet-500 to-indigo-500 text-white dark:border-violet-300/30",
  },
] as const satisfies readonly IntegrationChannel[];

export const INSTAGRAM_INTEGRATION_CHANNEL = INTEGRATION_CHANNELS[0];

export const AVAILABLE_INTEGRATION_CHANNELS = INTEGRATION_CHANNELS.filter(
  (channel) => channel.status === "available",
);

export const COMING_SOON_INTEGRATION_CHANNELS = INTEGRATION_CHANNELS.filter(
  (channel) => channel.status === "coming-soon",
);

export const REQUEST_INTEGRATION_CHANNEL = {
  name: {
    id: "integrations.catalog.request.name",
    defaultMessage: "Need another integration?",
  },
  iconLabel: "?",
  description: {
    id: "integrations.catalog.request.description",
    defaultMessage:
      "Tell us what you'd like to publish to next - we ship new channels every month.",
  },
  actionLabel: {
    id: "integrations.catalog.request.action",
    defaultMessage: "Request a channel",
  },
  accentClass:
    "border-slate-200 bg-gradient-to-br from-slate-100 via-slate-50 to-white text-slate-800 dark:border-slate-700 dark:from-slate-800 dark:via-slate-900 dark:to-slate-950 dark:text-slate-100",
} as const;
